import React from "react";
import { distinct, notEmpty } from "../../../../utils/common";
import { Dictionary } from "../../../../../entities/common";
import {
    TimesheetEditApplyResult,
    TimesheetEditUnitStatus,
    TimesheetEditWarning,
    TimesheetEditWarningCode
} from "../../../../../store/TimesheetApprovalStore";

export interface ProjectInfo {
    id: string;
    name: string;
}

export const customOptionKey = "__custom__";
export const projectWorkOptionKey = "__project-work__";
export const projectWorkLabel = "Project Work";
export const validateDebounceMs = 600;

export const guardListStyle: React.CSSProperties = { margin: "8px 0", paddingLeft: "20px", listStyleType: "disc" };

export const resolveWarningText = (warning: TimesheetEditWarning): string => {
    const names = warning.resourceNames.join(", ");
    const single = warning.resourceNames.length === 1;
    switch (warning.code) {
        case TimesheetEditWarningCode.ResourceNotCommitted:
            return single
                ? `Resource ${names} is not added to the target project's resource plan as a committed resource. Please add this resource to the target project's resource plan as a committed resource first.`
                : `Resources ${names} are not added to the target project's resource plan as committed resources. Please add these resources to the target project's resource plan as committed resources first.`;
        case TimesheetEditWarningCode.UnassignedTaskBlocked:
            return single
                ? `Resource ${names} is not assigned to the selected task. Please select a different task or assign the resource to the selected task first.`
                : `Resources ${names} are not assigned to the selected task. Please select a different task or assign these resources to the selected task first.`;
        case TimesheetEditWarningCode.UnassignedTaskAllowed:
            return single
                ? `Resource ${names} is not assigned to the selected task. Therefore, once moved, the time will be added to the timesheet but will not be synced to the project.`
                : `Resources ${names} are not assigned to the selected task. Therefore, once moved, the time will be added to the timesheet but will not be synced to the project.`;
        default:
            return "";
    }
};

export const groupWarningsByRow = (warnings: TimesheetEditWarning[]): Dictionary<TimesheetEditWarning[]> => {
    const grouped: Dictionary<TimesheetEditWarning[]> = {};
    for (const warning of warnings) {
        grouped[warning.sourceRowKey] = grouped[warning.sourceRowKey] ?? [];
        grouped[warning.sourceRowKey].push(warning);
    }
    return grouped;
};

export const buildApplyResultMessages = (result: TimesheetEditApplyResult): string[] => {
    const messages: string[] = [];
    const count = (status: TimesheetEditUnitStatus) => result.units.filter(_ => _.status === status).length;

    const skippedLocked = count(TimesheetEditUnitStatus.SkippedTargetHasLockedTimesheet);
    if (skippedLocked) {
        messages.push(
            `${skippedLocked} request${skippedLocked === 1 ? "" : "s"} could not be moved because the target project ` +
                "already has a submitted or approved timesheet for the same period that you do not have permission to recall."
        );
    }

    const skippedNoPermission = count(TimesheetEditUnitStatus.SkippedNoResourcePermission);
    if (skippedNoPermission) {
        messages.push(
            `${skippedNoPermission} timesheet${skippedNoPermission === 1 ? "" : "s"} could not be moved because you do ` +
                "not have permission to edit the time of the corresponding resources."
        );
    }

    const failed = result.units.filter(_ => _.status === TimesheetEditUnitStatus.Failed);
    if (failed.length) {
        messages.push(
            `${failed.length} timesheet${failed.length === 1 ? "" : "s"} could not be moved due to an error. Please try again.`
        );
    }

    return [
        ...messages,
        ...failed
            .map(_ => _.error)
            .filter(notEmpty)
            .filter(distinct)
    ];
};
